/**
 * ═══════════════════════════════════════════════════════════════════════════
 * QR — encodeur de codes QR, mode octet, correction M, versions 1 à 10.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur, 24/09/2026 : « cette page est accessible par QR code sans
 * connexion ». Il faut donc DESSINER un QR à l'écran de l'agent, pour que la
 * personne qui signe le scanne avec son téléphone.
 *
 * ⚠️⚠️ POURQUOI ICI, ET PAS `doctrust-render`. Le moteur d'impression sait
 * faire un QR — mais il ne rend QUE du PDF (`POST /render` → application/pdf).
 * Demander un PDF pour afficher un carré à l'écran ferait dépendre un écran
 * d'un conteneur tiers, d'un aller-retour réseau et de Chromium ; et la page
 * publique de signature, elle, n'a aucun moyen d'appeler le moteur.
 *
 * ⚠️ POURQUOI PAS `npm i qrcode`. Ce module vit des DEUX côtés : l'écran de
 * l'agent (navigateur) et, demain, une feuille de signatures imprimée côté
 * serveur. Le dépôt a déjà payé deux fois le prix d'une même règle écrite à
 * deux endroits (le SDK Double Ratchet forké, les trois catalogues de types de
 * document) : un module partagé sans dépendance sert les deux sans diverger.
 *
 * ⚠️⚠️ ET IL EST PROUVÉ CONTRE UNE IMPLÉMENTATION INDÉPENDANTE. Un QR faux ne
 * se voit pas : il ressemble trait pour trait à un QR juste, et c'est le
 * téléphone du citoyen qui découvre qu'il ne mène nulle part. Croire son
 * propre encodeur sur parole serait exactement l'illusion de fonctionnement
 * que ce dépôt traque. `tests/qr-contre-oracle.test.ts` confronte chaque
 * module de la matrice aux vecteurs produits par la bibliothèque `qrcode` du
 * conteneur `doctrust-render` — version, masque et les 3 249 modules de la
 * plus grande — et rougit au premier bit d'écart.
 *
 * ⚠️ BORNÉ, ET IL REFUSE AU LIEU DE DÉBORDER. Mode octet seul (le seul qui
 * encode une URL en minuscules et les accents), niveau M (celui que le moteur
 * d'impression emploie déjà), versions 1 à 10 — 213 octets, soit trois fois
 * l'URL de signature (71 caractères). Au-delà, on LÈVE : un QR tronqué en
 * silence serait indistinguable d'un QR valide.
 *
 * Référence : ISO/IEC 18004. Les tables ci-dessous en sont extraites pour le
 * seul niveau M ; y ajouter un niveau demande d'ajouter SA colonne partout,
 * jamais d'en déduire une par règle de trois.
 */

/** Niveau de correction employé — celui de `doctrust-render` (`server.js:23`). */
export const NIVEAU_CORRECTION = 'M' as const;

/** Versions couvertes. Au-delà, `matriceQr` lève. */
export const VERSION_MIN = 1;
export const VERSION_MAX = 10;

/**
 * Par version (index = version − 1), au niveau M :
 *   [ octets de données max, codemots de correction par bloc,
 *     [ nombre de blocs, codemots de données par bloc ] × groupes ]
 *
 * ⚠️ `capacite` N'EST PAS `blocs × donnees` : il faut en retrancher l'en-tête
 * (4 bits de mode + le compteur), soit 2 octets jusqu'à la version 9 et 3 à
 * partir de la 10, dont le compteur passe à 16 bits.
 */
const TABLE: Array<{ capacite: number; correction: number; groupes: Array<[number, number]> }> = [
  { capacite: 14,  correction: 10, groupes: [[1, 16]] },                 // v1
  { capacite: 26,  correction: 16, groupes: [[1, 28]] },                 // v2
  { capacite: 42,  correction: 26, groupes: [[1, 44]] },                 // v3
  { capacite: 62,  correction: 18, groupes: [[2, 32]] },                 // v4
  { capacite: 84,  correction: 24, groupes: [[2, 43]] },                 // v5
  { capacite: 106, correction: 16, groupes: [[4, 27]] },                 // v6
  { capacite: 122, correction: 18, groupes: [[4, 31]] },                 // v7
  { capacite: 152, correction: 22, groupes: [[2, 38], [2, 39]] },        // v8
  { capacite: 180, correction: 22, groupes: [[3, 36], [2, 37]] },        // v9
  { capacite: 213, correction: 26, groupes: [[4, 43], [1, 44]] },        // v10
];

/** Centres des motifs d'alignement, par version. La v1 n'en porte aucun. */
const ALIGNEMENTS: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30],
  [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

// ── corps de Galois GF(256), polynôme générateur 0x11D ──────────────────────

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255]!;
}

const multiplier = (a: number, b: number): number =>
  (a === 0 || b === 0) ? 0 : EXP[LOG[a]! + LOG[b]!]!;

/** Polynôme générateur de degré `degre`, produit des (x − α^i). */
function generateur(degre: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degre; i += 1) {
    const suivant = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j += 1) {
      /* ⚠️ L'ORDRE N'EST PAS SYMÉTRIQUE. Les coefficients sont rangés du degré
         le PLUS HAUT au plus bas : multiplier par (x + α^i) décale p vers le
         haut (`suivant[j] ^= poly[j]`) et ajoute α^i·p un cran plus bas.
         Inverser les deux donne le polynôme RETOURNÉ — identique pour i = 0,
         donc un essai sur un seul degré ne voit rien, et le code produit se
         dessine sans qu'aucune structure ne cloche. */
      suivant[j] = (suivant[j]! ^ poly[j]!);
      suivant[j + 1] = (suivant[j + 1]! ^ multiplier(poly[j]!, EXP[i]!));
    }
    poly = suivant;
  }
  return poly;
}

/** Reste de la division du bloc par le générateur — les codemots de correction. */
function correction(bloc: Uint8Array, nombre: number): Uint8Array {
  const gen = generateur(nombre);
  const reste = new Uint8Array(bloc.length + nombre);
  reste.set(bloc);
  for (let i = 0; i < bloc.length; i += 1) {
    const tete = reste[i]!;
    if (tete === 0) continue;
    for (let j = 0; j < gen.length; j += 1) {
      reste[i + j] = reste[i + j]! ^ multiplier(gen[j]!, tete);
    }
  }
  return reste.slice(bloc.length);
}

// ── encodage des données ────────────────────────────────────────────────────

/** ⚠️ UTF-8, jamais latin-1 : « éàçùè » d'un acte doit survivre au scan. */
function octetsDe(texte: string): Uint8Array {
  return new TextEncoder().encode(texte);
}

class Bits {
  private readonly bits: number[] = [];
  pousser(valeur: number, longueur: number): void {
    for (let i = longueur - 1; i >= 0; i -= 1) this.bits.push((valeur >> i) & 1);
  }
  get longueur(): number { return this.bits.length; }
  enOctets(taille: number): Uint8Array {
    const out = new Uint8Array(taille);
    for (let i = 0; i < this.bits.length; i += 1) {
      if (this.bits[i]) out[i >> 3] = out[i >> 3]! | (0x80 >> (i & 7));
    }
    return out;
  }
}

function versionPour(octets: number): number {
  for (let v = VERSION_MIN; v <= VERSION_MAX; v += 1) {
    if (octets <= TABLE[v - 1]!.capacite) return v;
  }
  throw new Error(
    `QR : ${octets} octets à encoder, la version ${VERSION_MAX} n'en porte que `
    + `${TABLE[VERSION_MAX - 1]!.capacite} au niveau ${NIVEAU_CORRECTION}. `
    + `Aucun code n'est produit : un QR tronqué se scanne et mène ailleurs.`);
}

/** Codemots finaux : données et correction entrelacées, blocs compris. */
function codemots(octets: Uint8Array, version: number): Uint8Array {
  const t = TABLE[version - 1]!;
  const bits = new Bits();
  bits.pousser(0b0100, 4);                                   // mode octet
  bits.pousser(octets.length, version >= 10 ? 16 : 8);       // compteur
  for (const o of octets) bits.pousser(o, 8);

  const totalDonnees = t.groupes.reduce((s, [n, d]) => s + n * d, 0);
  // Terminateur : jusqu'à 4 bits à zéro, puis alignement sur l'octet.
  const restant = totalDonnees * 8 - bits.longueur;
  bits.pousser(0, Math.min(4, restant));
  if (bits.longueur % 8 !== 0) bits.pousser(0, 8 - (bits.longueur % 8));

  const donnees = bits.enOctets(totalDonnees);
  // Remplissage alterné 0xEC / 0x11, imposé par la norme.
  for (let i = bits.longueur / 8, alterne = 0; i < totalDonnees; i += 1, alterne += 1) {
    donnees[i] = alterne % 2 === 0 ? 0xec : 0x11;
  }

  const blocsDonnees: Uint8Array[] = [];
  const blocsCorrection: Uint8Array[] = [];
  let curseur = 0;
  for (const [nombre, taille] of t.groupes) {
    for (let i = 0; i < nombre; i += 1) {
      const bloc = donnees.slice(curseur, curseur + taille);
      curseur += taille;
      blocsDonnees.push(bloc);
      blocsCorrection.push(correction(bloc, t.correction));
    }
  }

  /* ⚠️ L'ENTRELACEMENT SE FAIT PAR COLONNE, et les blocs n'ont pas tous la
     même longueur (v8 : 38 et 39). Lire en ligne produirait un code que rien
     ne signale et qu'aucun lecteur ne décode. */
  const sortie: number[] = [];
  const plusLong = Math.max(...blocsDonnees.map((b) => b.length));
  for (let i = 0; i < plusLong; i += 1) {
    for (const b of blocsDonnees) if (i < b.length) sortie.push(b[i]!);
  }
  for (let i = 0; i < t.correction; i += 1) {
    for (const b of blocsCorrection) sortie.push(b[i]!);
  }
  return new Uint8Array(sortie);
}

// ── construction de la matrice ──────────────────────────────────────────────

type Grille = { taille: number; modules: Int8Array; reserve: Uint8Array };

function grilleVide(version: number): Grille {
  const taille = version * 4 + 17;
  return { taille, modules: new Int8Array(taille * taille), reserve: new Uint8Array(taille * taille) };
}

const poser = (g: Grille, x: number, y: number, noir: boolean, fige = true): void => {
  g.modules[y * g.taille + x] = noir ? 1 : 0;
  if (fige) g.reserve[y * g.taille + x] = 1;
};
const lire = (g: Grille, x: number, y: number): boolean => g.modules[y * g.taille + x] === 1;
const figee = (g: Grille, x: number, y: number): boolean => g.reserve[y * g.taille + x] === 1;

function motifDetection(g: Grille, ox: number, oy: number): void {
  for (let y = -1; y <= 7; y += 1) {
    for (let x = -1; x <= 7; x += 1) {
      const cx = ox + x; const cy = oy + y;
      if (cx < 0 || cy < 0 || cx >= g.taille || cy >= g.taille) continue;
      /* ⚠️ LA BORDURE NE VAUT QU'À L'INTÉRIEUR DU MOTIF 7×7. Sans `dansMotif`,
         `y === 0` noircit aussi la case x = 7 — qui est le SÉPARATEUR, et
         doit rester blanche. Le code se dessine alors normalement et aucun
         lecteur ne trouve plus le motif de détection. */
      const dansMotif = x >= 0 && x <= 6 && y >= 0 && y <= 6;
      const bord = dansMotif && (x === 0 || x === 6 || y === 0 || y === 6);
      const coeur = x >= 2 && x <= 4 && y >= 2 && y <= 4;
      poser(g, cx, cy, bord || coeur);
    }
  }
}

function structure(g: Grille, version: number): void {
  motifDetection(g, 0, 0);
  motifDetection(g, g.taille - 7, 0);
  motifDetection(g, 0, g.taille - 7);

  // Rythmes horizontal et vertical.
  for (let i = 8; i < g.taille - 8; i += 1) {
    poser(g, i, 6, i % 2 === 0);
    poser(g, 6, i, i % 2 === 0);
  }

  // Motifs d'alignement — jamais sur un motif de détection.
  const centres = ALIGNEMENTS[version - 1]!;
  for (const cy of centres) {
    for (const cx of centres) {
      const surDetection = (cx === 6 && cy === 6)
        || (cx === 6 && cy === g.taille - 7) || (cx === g.taille - 7 && cy === 6);
      if (surDetection) continue;
      for (let y = -2; y <= 2; y += 1) {
        for (let x = -2; x <= 2; x += 1) {
          poser(g, cx + x, cy + y, Math.max(Math.abs(x), Math.abs(y)) !== 1);
        }
      }
    }
  }

  // Module sombre — toujours noir, toujours à cette place.
  poser(g, 8, g.taille - 8, true);

  // Réservation des zones d'information de format (remplies après le masque).
  for (let i = 0; i < 9; i += 1) {
    if (i !== 6) { poser(g, i, 8, false); poser(g, 8, i, false); }
  }
  for (let i = 0; i < 8; i += 1) {
    poser(g, g.taille - 1 - i, 8, false);
    if (i < 7) poser(g, 8, g.taille - 1 - i, false);
  }

  if (version >= 7) {
    const info = infoVersion(version);
    for (let i = 0; i < 18; i += 1) {
      const bit = ((info >> i) & 1) === 1;
      poser(g, Math.floor(i / 3), g.taille - 11 + (i % 3), bit);
      poser(g, g.taille - 11 + (i % 3), Math.floor(i / 3), bit);
    }
  }
}

/** BCH(18,6) — 6 bits de version, 12 de contrôle. */
function infoVersion(version: number): number {
  let reste = version << 12;
  for (let i = 0; i < 12; i += 1) {
    if ((reste >> (17 - i)) & 1) reste ^= 0x1f25 << (5 - i);
  }
  return (version << 12) | (reste & 0xfff);
}

/** BCH(15,5) puis masque 0x5412 — niveau M = 0b00. */
function infoFormat(masque: number): number {
  const donnees = (0b00 << 3) | masque;
  let reste = donnees << 10;
  for (let i = 0; i < 5; i += 1) {
    if ((reste >> (14 - i)) & 1) reste ^= 0x537 << (4 - i);
  }
  return ((donnees << 10) | (reste & 0x3ff)) ^ 0x5412;
}

function poserFormat(g: Grille, masque: number): void {
  const info = infoFormat(masque);
  for (let i = 0; i < 15; i += 1) {
    const bit = ((info >> i) & 1) === 1;
    // Copie 1 — autour du motif haut-gauche.
    if (i < 6) poser(g, 8, i, bit);
    else if (i === 6) poser(g, 8, 7, bit);
    else if (i === 7) poser(g, 8, 8, bit);
    else if (i === 8) poser(g, 7, 8, bit);
    else poser(g, 14 - i, 8, bit);
    // Copie 2 — le long des deux autres motifs.
    if (i < 8) poser(g, g.taille - 1 - i, 8, bit);
    else poser(g, 8, g.taille - 15 + i, bit);
  }
}

/** Parcours en zigzag, de bas à droite vers le haut, colonne de rythme sautée. */
function poserDonnees(g: Grille, mots: Uint8Array): void {
  let bit = 0;
  let montant = true;
  for (let droite = g.taille - 1; droite >= 1; droite -= 2) {
    if (droite === 6) droite = 5;                       // la colonne 6 est le rythme
    for (let pas = 0; pas < g.taille; pas += 1) {
      const y = montant ? g.taille - 1 - pas : pas;
      for (const x of [droite, droite - 1]) {
        if (figee(g, x, y)) continue;
        const octet = mots[bit >> 3];
        const noir = octet !== undefined && ((octet >> (7 - (bit & 7))) & 1) === 1;
        poser(g, x, y, noir, false);
        bit += 1;
      }
    }
    montant = !montant;
  }
}

const MASQUES: Array<(x: number, y: number) => boolean> = [
  (x, y) => (y + x) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (y + x) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((y * x) % 2) + ((y * x) % 3) === 0,
  (x, y) => (((y * x) % 2) + ((y * x) % 3)) % 2 === 0,
  (x, y) => (((y + x) % 2) + ((y * x) % 3)) % 2 === 0,
];

/** Les quatre pénalités de la norme — c'est leur somme qui élit le masque. */
function penalite(g: Grille): number {
  const n = g.taille;
  let total = 0;

  // N1 — séries de cinq modules de même couleur et plus.
  for (let i = 0; i < n; i += 1) {
    for (const enLigne of [true, false]) {
      let serie = 1;
      let precedent = enLigne ? lire(g, 0, i) : lire(g, i, 0);
      for (let j = 1; j < n; j += 1) {
        const actuel = enLigne ? lire(g, j, i) : lire(g, i, j);
        if (actuel === precedent) serie += 1;
        else { if (serie >= 5) total += 3 + (serie - 5); serie = 1; precedent = actuel; }
      }
      if (serie >= 5) total += 3 + (serie - 5);
    }
  }

  // N2 — carrés 2×2 d'une seule couleur.
  for (let y = 0; y < n - 1; y += 1) {
    for (let x = 0; x < n - 1; x += 1) {
      const a = lire(g, x, y);
      if (a === lire(g, x + 1, y) && a === lire(g, x, y + 1) && a === lire(g, x + 1, y + 1)) total += 3;
    }
  }

  /* N3 — le motif 1:1:3:1:1 précédé OU suivi de quatre modules clairs.
     ⚠️ FENÊTRE GLISSANTE DE 11 MODULES, ENTIÈREMENT DANS LE SYMBOLE. Première
     version : je complétais les bords par du clair, en me disant que la zone
     de silence est claire. C'est défendable et ce n'est pas ce que font les
     lecteurs : la norme se lit sur le symbole, et compléter les bords ajoute
     des pénalités fantômes qui élisent un autre masque. */
  for (let i = 0; i < n; i += 1) {
    let ligne = 0;
    let colonne = 0;
    for (let j = 0; j < n; j += 1) {
      ligne = ((ligne << 1) & 0x7ff) | (lire(g, j, i) ? 1 : 0);
      if (j >= 10 && (ligne === 0x5d0 || ligne === 0x05d)) total += 40;
      colonne = ((colonne << 1) & 0x7ff) | (lire(g, i, j) ? 1 : 0);
      if (j >= 10 && (colonne === 0x5d0 || colonne === 0x05d)) total += 40;
    }
  }

  /* N4 — écart de la proportion de sombre à la moitié, par pas de 5 %.
     ⚠️ `ceil`, PAS `floor` : à 51 % de sombre, `floor` rend 0 et `ceil` rend
     une pénalité de 10. Les deux produisent un code lisible — le masque n'est
     qu'une heuristique de QUALITÉ — mais seul `ceil` reproduit le choix des
     lecteurs éprouvés, et c'est ce qui rend l'essai contre l'oracle probant. */
  let noirs = 0;
  for (let i = 0; i < n * n; i += 1) if (g.modules[i] === 1) noirs += 1;
  total += Math.abs(Math.ceil((noirs * 100) / (n * n) / 5) - 10) * 10;
  return total;
}

/**
 * La matrice d'un texte. `modules[y * taille + x]` vaut vrai pour un module noir.
 *
 * ⚠️ AUCUNE MARGE N'EST INCLUSE. La norme en exige quatre modules tout autour
 * (« zone de silence ») ; c'est le rendu qui l'ajoute, parce qu'une marge
 * incluse ici se retrouverait comptée deux fois par l'appelant qui en ajoute.
 */
export function matriceQr(texte: string): {
  taille: number; modules: boolean[]; version: number; masque: number;
} {
  if (texte.length === 0) {
    throw new Error("QR : rien à encoder. Un code vide se scanne et ne mène nulle part.");
  }
  const octets = octetsDe(texte);
  const version = versionPour(octets.length);
  const mots = codemots(octets, version);

  let meilleure: Grille | null = null;
  let meilleurMasque = 0;
  let meilleurScore = Infinity;
  for (let m = 0; m < 8; m += 1) {
    const g = grilleVide(version);
    structure(g, version);
    poserDonnees(g, mots);
    /* Le masque ne touche QUE les modules de données — jamais la structure. */
    for (let y = 0; y < g.taille; y += 1) {
      for (let x = 0; x < g.taille; x += 1) {
        if (!figee(g, x, y) && MASQUES[m]!(x, y)) {
          g.modules[y * g.taille + x] = g.modules[y * g.taille + x] === 1 ? 0 : 1;
        }
      }
    }
    poserFormat(g, m);
    const score = penalite(g);
    if (score < meilleurScore) { meilleurScore = score; meilleure = g; meilleurMasque = m; }
  }

  const g = meilleure!;
  const modules: boolean[] = new Array(g.taille * g.taille);
  for (let i = 0; i < modules.length; i += 1) modules[i] = g.modules[i] === 1;
  return { taille: g.taille, modules, version, masque: meilleurMasque };
}

/**
 * Le QR en SVG, prêt à poser dans une page ou un gabarit.
 *
 * ⚠️ UN SEUL `<path>`, PAS UN `<rect>` PAR MODULE. Une v10 compte 3 249
 * modules : autant de nœuds ferait ramer l'écran et gonflerait tout PDF qui
 * l'embarque. Le chemin est en coordonnées de MODULE, mises à l'échelle par
 * le `viewBox` — un QR reste net à toute taille.
 */
export function qrEnSvg(
  texte: string,
  { marge = 4, titre }: { marge?: number; titre?: string } = {},
): string {
  const { taille, modules } = matriceQr(texte);
  const cote = taille + marge * 2;
  let chemin = '';
  for (let y = 0; y < taille; y += 1) {
    let x = 0;
    while (x < taille) {
      if (!modules[y * taille + x]) { x += 1; continue; }
      let large = 1;
      while (x + large < taille && modules[y * taille + x + large]) large += 1;
      chemin += `M${x + marge} ${y + marge}h${large}v1h-${large}z`;
      x += large;
    }
  }
  const etiquette = titre
    ? `<title>${titre.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]!))}</title>`
    : '';
  /* ⚠️ Le fond BLANC est dessiné, jamais laissé transparent : sur un écran
     sombre, un QR sans fond n'a plus de contraste et ne se scanne pas. */
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cote} ${cote}" `
    + `shape-rendering="crispEdges" role="img">${etiquette}`
    + `<rect width="${cote}" height="${cote}" fill="#fff"/>`
    + `<path d="${chemin}" fill="#000"/></svg>`;
}
